/**
 * Marken, Bereiche und der Endpunkt (HM-054).
 *
 * Alles, was **neben** den Reihen im Bild steht, zeichnet dieses Zusatzstück.
 * Zwei Dinge sind fachlich verlangt und keine Verzierung:
 *
 * **Ein Bereich ist etwas anderes als eine Lücke.** Vor der
 * Aufbewahrungsgrenze eines Datenpunkts fehlen die Werte nicht, weil das Gerät
 * geschwiegen hätte — sie sind planmäßig gelöscht (HM-048). Diese Zeit bekommt
 * eine **Schraffur** und keine leere Fläche: Eine leere Fläche liest sich als
 * tagelanger Ausfall.
 *
 * **Eine senkrechte Marke trennt zwei Abschnitte.** Der Zählerwechsel (HM-011)
 * ist der Fall, für den es sie gibt: Links und rechts davon zählt ein anderes
 * Gerät, und eine Differenz über die Marke hinweg ist keine Verbrauchsmenge
 * (Regel 4).
 *
 * **Der Endpunkt steht nur bei einer einzelnen Reihe.** Er sagt „das ist der
 * letzte Stand"; bei fünf Reihen wären es fünf Punkte, die einander die Aussage
 * nehmen.
 */
import uPlot from "uplot";
import type { ChartPalette } from "./palette.js";
import { withAlpha } from "./palette.js";
import type { ChartAnnotation } from "./types.js";

export interface DecorationState {
  readonly annotations: readonly ChartAnnotation[];
  readonly palette: ChartPalette;
  /** Welche uPlot-Reihe ihren letzten Wert markiert bekommt — sonst `null`. */
  readonly endpoint: { readonly seriesIdx: number; readonly color: string } | null;
}

/** Die Schraffur, einmal je Farbe erzeugt und dann behalten. */
const HATCH_CACHE = new Map<string, CanvasPattern | null>();

/**
 * Eine Schraffur aus feinen Diagonalen.
 *
 * Sie ist der Grund, warum ein gelöschter Zeitraum ohne Legende verständlich
 * ist: Eine Fläche, die *bemustert* ist, wird als „hier gilt etwas anderes"
 * gelesen, während eine eingefärbte Fläche wie eine Messgröße aussieht. Sie
 * trägt außerdem durch, wo Farbe nicht ankommt — Ausdruck, Farbfehlsichtigkeit,
 * erzwungene Kontrastdarstellung.
 */
function hatch(ctx: CanvasRenderingContext2D, color: string): CanvasPattern | null {
  const cached = HATCH_CACHE.get(color);
  if (cached !== undefined) return cached;

  const size = 6 * uPlot.pxRatio;
  const tile = document.createElement("canvas");
  tile.width = size;
  tile.height = size;
  const tileCtx = tile.getContext("2d");
  if (tileCtx === null) return null;

  tileCtx.strokeStyle = color;
  tileCtx.lineWidth = uPlot.pxRatio;
  tileCtx.beginPath();
  tileCtx.moveTo(0, size);
  tileCtx.lineTo(size, 0);
  tileCtx.stroke();

  const pattern = ctx.createPattern(tile, "repeat");
  HATCH_CACHE.set(color, pattern);
  return pattern;
}

export function decorationsPlugin(read: () => DecorationState): uPlot.Plugin {
  return {
    hooks: {
      /**
       * Bereiche liegen **unter** den Reihen: Eine Schraffur über einer Kurve
       * würde die Kurve schwerer lesbar machen, und die Aussage gilt dem
       * Hintergrund, nicht den Werten.
       */
      drawClear: (u) => {
        const { annotations, palette } = read();
        const spans = annotations.filter((mark) => mark.until !== undefined);
        if (spans.length === 0) return;

        const { ctx, bbox } = u;
        const pattern = hatch(ctx, withAlpha(palette.text, 0.5));
        if (pattern === null) return;

        ctx.save();
        ctx.beginPath();
        ctx.rect(bbox.left, bbox.top, bbox.width, bbox.height);
        ctx.clip();
        ctx.fillStyle = pattern;

        for (const mark of spans) {
          const from = u.valToPos(mark.at, "x", true);
          const to = u.valToPos(mark.until!, "x", true);
          ctx.fillRect(Math.min(from, to), bbox.top, Math.abs(to - from), bbox.height);
        }

        ctx.restore();
      },

      draw: (u) => {
        const { annotations, palette, endpoint } = read();
        const { ctx, bbox } = u;

        ctx.save();
        ctx.font = `${Math.round(11 * uPlot.pxRatio)}px ${palette.font}`;
        ctx.textBaseline = "top";
        /**
         * **Ausdrücklich links ausgerichtet.** Der Zeichenzustand kommt von
         * uPlot, das gerade die y-Achse rechtsbündig beschriftet hat — ohne
         * diese Zeile endete die Marke an ihrer x-Position, statt dort zu
         * beginnen, und „nicht mehr aufbewahrt" stand als „fbewahrt" halb
         * außerhalb des Bildes.
         */
        ctx.textAlign = "left";

        for (const mark of annotations) {
          const tone = mark.tone === "warning" ? palette.warning : palette.muted;
          const x = Math.round(u.valToPos(mark.at, "x", true));

          if (mark.until === undefined) {
            ctx.strokeStyle = tone;
            ctx.lineWidth = uPlot.pxRatio;
            // Gestrichelt, weil die Linie **keine** Messreihe ist. Eine durchgezogene
            // senkrechte Linie liest sich sonst als Wert.
            ctx.setLineDash([4 * uPlot.pxRatio, 3 * uPlot.pxRatio]);
            ctx.beginPath();
            ctx.moveTo(x + 0.5, bbox.top);
            ctx.lineTo(x + 0.5, bbox.top + bbox.height);
            ctx.stroke();
            ctx.setLineDash([]);
          }

          /**
           * Die Beschriftung wandert an den Rand statt hinauszulaufen: Eine
           * Marke am rechten Rand ist der Regelfall (die Aufbewahrungsgrenze
           * liegt links, der letzte Wechsel oft rechts), und abgeschnittener
           * Text sagt weniger als gar keiner.
           */
          const width = ctx.measureText(mark.label).width;
          const right = bbox.left + bbox.width;
          const at = Math.min(Math.max(x + 4 * uPlot.pxRatio, bbox.left), right - width);
          ctx.fillStyle = tone;
          ctx.fillText(mark.label, at, bbox.top + 2 * uPlot.pxRatio);
        }

        if (endpoint !== null) {
          const values = u.data[endpoint.seriesIdx];
          const index = lastKnown(values);
          if (index !== null) {
            const x = u.valToPos(u.data[0]![index]!, "x", true);
            const y = u.valToPos(values![index] as number, "y", true);
            ctx.beginPath();
            ctx.arc(x, y, 4 * uPlot.pxRatio, 0, Math.PI * 2);
            ctx.fillStyle = endpoint.color;
            ctx.fill();
            // Ein Ring in der Grundfarbe setzt den Punkt von der Kurve ab, auf
            // der er sitzt.
            ctx.lineWidth = 2 * uPlot.pxRatio;
            ctx.strokeStyle = read().palette.surface;
            ctx.stroke();
          }
        }

        ctx.restore();
      },
    },
  };
}

/** Der Index des letzten Werts, der einer ist — Lücken am Ende zählen nicht. */
function lastKnown(values: ArrayLike<number | null | undefined> | undefined): number | null {
  if (values === undefined) return null;
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value !== null && value !== undefined && Number.isFinite(value)) return index;
  }
  return null;
}
